import { useState } from 'react';
import avatarFamily from '../assets/landing/organizer/avatar-family.png';
import avatarJess from '../assets/landing/organizer/avatar-jess.png';
import avatarMaya from '../assets/landing/organizer/avatar-maya.png';
import avatarNoah from '../assets/landing/organizer/avatar-noah.png';
import avatarTaylor from '../assets/landing/organizer/avatar-taylor.png';
import centerHeart from '../assets/landing/organizer/center-heart.png';
import iconBell from '../assets/landing/organizer/icon-bell.png';
import iconCalendar from '../assets/landing/organizer/icon-calendar.png';
import iconClock from '../assets/landing/organizer/icon-clock.png';
import iconForYou from '../assets/landing/organizer/icon-for-you.png';
import iconGroupRules from '../assets/landing/organizer/icon-group-rules.png';
import iconLink from '../assets/landing/organizer/icon-link.png';
import iconLock from '../assets/landing/organizer/icon-lock.png';
import iconNoApp from '../assets/landing/organizer/icon-no-app.png';
import iconPlansMotion from '../assets/landing/organizer/icon-plans-motion.png';
import iconSmartNudges from '../assets/landing/organizer/icon-smart-nudges.png';
import iconSuggestedPeople from '../assets/landing/organizer/icon-suggested-people.png';
import iconTimeMatching from '../assets/landing/organizer/icon-time-matching.png';

const organizerFeatures = [
  [iconSmartNudges, 'Smart nudges', 'Gentle reminders to keep connections strong.'],
  [iconSuggestedPeople, 'Suggested people', 'Get ideas for who you haven’t seen in a while.'],
  [iconGroupRules, 'Group rules', 'Set availability windows and preferences.'],
  [iconTimeMatching, 'Time matching', 'CatchUp finds the best overlap for everyone.'],
  [iconPlansMotion, 'Plans in motion', 'See what’s confirmed and what’s coming up next.'],
] as const;

const guestFeatures = [
  [iconNoApp, 'No app required', 'Guests respond right from their browser.'],
  [iconLink, 'Magic link invite', 'One link is all it takes to get started.'],
  [iconClock, 'Pick availability in seconds', 'Quick, simple, and mobile friendly.'],
  [iconLock, 'Private responses', 'Your times are only used to find the best plan.'],
  [iconBell, 'Get notified', 'You’ll know when the plan is confirmed.'],
] as const;

const people = [
  ['Maya', '24 days', avatarMaya],
  ['Jess', '31 days', avatarJess],
  ['Noah', '47 days', avatarNoah],
  ['Taylor', '63 days', avatarTaylor],
] as const;

const availabilityOptions = [
  ['Fri, May 23', '6:00 PM', false],
  ['Sat, May 24', '11:00 AM', true],
  ['Sat, May 24', '1:00 PM', true],
  ['Sun, May 25', '10:00 AM', false],
] as const;

function MiniIcon({ children }: { children: string }) {
  return (
    <span
      aria-hidden="true"
      className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[10px] bg-[#FFF0ED] text-[15px] font-black text-[#FF625B]"
    >
      {children}
    </span>
  );
}

export function OrganizerSection() {
  const [selectedTimes, setSelectedTimes] = useState(
    () => new Set(availabilityOptions.filter(([, , selected]) => selected).map(([day, time]) => `${day} ${time}`)),
  );
  const [status, setStatus] = useState('Organizer section ready');

  const toggleTime = (label: string) => {
    setSelectedTimes((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
        setStatus(`${label} removed`);
      } else {
        next.add(label);
        setStatus(`${label} selected`);
      }
      return next;
    });
  };

  return (
    <section
      aria-labelledby="organizer-title"
      className="relative overflow-hidden bg-[radial-gradient(circle_at_50%_47%,rgba(246,242,255,0.75),rgba(255,255,255,0)_38%),linear-gradient(180deg,#ffffff_0%,#fcfbff_55%,#ffffff_100%)] px-5 pb-16 pt-12 text-[#090F3D] md:px-8 md:pb-20 md:pt-14"
      id="for-friends"
    >
      <div className="mx-auto max-w-[1430px]">
        <div className="grid justify-items-center text-center">
          <p className="inline-flex min-h-[42px] items-center gap-2.5 rounded-full bg-[#F1EDFF] px-5 text-[18px] font-black leading-none text-[#3B23FF]">
            <span aria-hidden="true">♧</span>
            Two sides of every great plan
          </p>
          <h2
            className="mt-[18px] max-w-[clamp(40rem,61vw,58rem)] font-serif text-[clamp(3.25rem,5.15vw,5.35rem)] font-black leading-[1.02] tracking-normal text-[#090F3D]"
            id="organizer-title"
          >
            Easy for organizers.
            <br />
            <span className="text-[#F46662]">Effortless for guests.</span>
          </h2>
          <p className="mt-[18px] max-w-[636px] text-[20px] font-semibold leading-[1.45] text-[#4D5577] sm:text-[22px]">
            CatchUp makes it simple to plan meaningful time together--whether you’re organizing or joining.
          </p>
        </div>

        <div
          className="mt-[clamp(1.5rem,3vw,3rem)] grid justify-center gap-7 md:grid-cols-2 md:items-start xl:grid-cols-[minmax(13rem,1fr)_minmax(17.75rem,1.08fr)_minmax(3.5rem,0.34fr)_minmax(17rem,1fr)_minmax(14rem,1fr)] xl:gap-[clamp(12px,1.3vw,20px)]"
          data-testid="organizer-layout"
        >
          <div className="order-1 w-full max-w-[620px] md:justify-self-end xl:max-w-none xl:justify-self-center">
            <div className="flex min-h-[74px] items-center gap-[18px]">
              <img alt="" aria-hidden="true" className="h-[58px] w-[58px] object-contain" src={iconForYou} />
              <div>
                <h3 className="text-[25px] font-black leading-[1.1]">For you</h3>
                <p className="mt-[7px] text-[17px] font-semibold leading-[1.35] text-[#495074] xl:whitespace-nowrap">
                  Everything you need to plan with ease
                </p>
              </div>
            </div>

            <div aria-label="Organizer benefits" className="mt-[5px] grid w-full max-w-[260px]">
              {organizerFeatures.map(([icon, title, body]) => (
                <article className="grid min-h-24 grid-cols-[58px_1fr] items-center gap-[18px] border-b border-[#E2E4F1] text-left" key={title}>
                  <img alt="" aria-hidden="true" className="h-[58px] w-[58px] object-contain" src={icon} />
                  <div>
                    <h4 className="text-[17px] font-black leading-[1.1]">{title}</h4>
                    <p className="mt-1.5 text-[15px] font-semibold leading-[1.45] text-[#495074]">{body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="order-2 mt-0 w-full max-w-[clamp(17.75rem,21vw,21.75rem)] md:justify-self-start xl:mt-[clamp(3.75rem,5.4vw,5.5rem)] xl:justify-self-center" data-testid="organizer-mockup">
            <div className="min-h-[474px] rounded-[18px] border border-[#DEE1F0]/80 bg-white/90 bg-[radial-gradient(circle_at_34%_5%,rgba(238,231,255,0.6),rgba(255,255,255,0)_42%)] px-4 pb-[15px] pt-[23px] shadow-[0_18px_48px_rgba(62,54,125,0.10)]">
              <h3 className="text-[20px] font-black leading-[1.2]">Good morning, Alex 👋</h3>
              <p className="mt-2 text-[14px] font-semibold text-[#3F456B]">Let’s get something on the calendar.</p>

              <section aria-label="People you have not seen lately" className="mt-[18px] rounded-xl bg-white/95 px-[13px] py-[11px] shadow-[0_12px_28px_rgba(68,60,122,0.07)]">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h4 className="text-[12px] font-black leading-tight">People you haven’t seen lately</h4>
                  <button className="inline-flex min-h-11 min-w-11 items-center justify-center text-[12px] font-black text-[#3D22FF]" type="button">
                    View all
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-[11px]">
                  {people.map(([name, days, avatar]) => (
                    <button className="min-h-[82px] rounded-xl text-center" key={name} type="button">
                      <img alt={`${name} avatar`} className="mx-auto mb-2 h-12 w-12 rounded-full object-cover" src={avatar} />
                      <span className="block text-[12px] font-black leading-tight">{name}</span>
                      <span className="mt-1.5 block text-[11px] font-semibold text-[#485076]">{days}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section aria-label="Upcoming plans" className="mt-2.5 rounded-xl bg-white/95 px-[13px] py-[11px] shadow-[0_12px_28px_rgba(68,60,122,0.07)]">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h4 className="text-[12px] font-black leading-tight">Upcoming plans</h4>
                  <button className="inline-flex min-h-11 min-w-11 items-center justify-center text-[12px] font-black text-[#3D22FF]" type="button">
                    View all
                  </button>
                </div>
                <button className="grid min-h-11 w-full grid-cols-[28px_1fr_26px] items-center gap-2.5 text-left" type="button">
                  <MiniIcon>☆</MiniIcon>
                  <span>
                    <span className="block text-[14px] font-black leading-tight">Brunch with Jess</span>
                    <span className="mt-1 block text-[13px] font-semibold leading-tight text-[#465071]">Sat, May 24 · 11:00 AM</span>
                  </span>
                  <MiniIcon>▣</MiniIcon>
                </button>
              </section>

              <section aria-label="Your groups" className="mt-2.5 rounded-xl bg-white/95 px-[13px] py-[11px] shadow-[0_12px_28px_rgba(68,60,122,0.07)]">
                <h4 className="mb-2 text-[12px] font-black leading-tight">Your groups</h4>
                <div className="-mx-[13px] mb-2 h-px bg-[#E2E4F1]" />
                <button className="grid min-h-[46px] w-full grid-cols-[44px_1fr_20px] items-center gap-2.5 text-left" type="button">
                  <img alt="Family group" className="h-[42px] w-[42px] rounded-full object-cover" src={avatarFamily} />
                  <span>
                    <span className="block text-[14px] font-black leading-tight">Family</span>
                    <span className="mt-1 block text-[13px] font-semibold leading-tight text-[#465071]">Next: Sun, May 25</span>
                  </span>
                  <span aria-hidden="true" className="text-3xl leading-none text-[#35385F]">›</span>
                </button>
              </section>
            </div>
          </div>

          <div className="relative order-3 hidden min-h-[540px] w-full xl:block">
            <div className="absolute bottom-0 left-1/2 top-[18px] -translate-x-1/2 border-l-2 border-dashed border-[#D5D9EA]" />
            <img
              alt=""
              aria-hidden="true"
              className="absolute left-1/2 top-[248px] h-[102px] w-[102px] -translate-x-1/2 object-contain"
              src={centerHeart}
            />
          </div>

          <div className="order-4 w-full max-w-[310px] md:justify-self-end xl:justify-self-center">
            <div className="flex min-h-[74px] items-center gap-[18px] xl:ml-[34px]">
              <img alt="" aria-hidden="true" className="h-[58px] w-[58px] object-contain" src={iconNoApp} />
              <div>
                <h3 className="text-[25px] font-black leading-[1.1]">For guests</h3>
                <p className="mt-[7px] text-[17px] font-semibold leading-[1.35] text-[#495074] xl:whitespace-nowrap">
                  No hassle. No signup. No stress.
                </p>
              </div>
            </div>

            <div className="mx-auto mt-1.5 min-h-[477px] w-full max-w-[clamp(17rem,20vw,20.5rem)] rounded-2xl border border-[#DEE1F0]/90 bg-white/95 px-4 pb-6 pt-[15px] text-center shadow-[0_26px_70px_rgba(62,54,125,0.12)] xl:ml-[clamp(-0.75rem,-0.7vw,-0.25rem)] xl:mr-0">
              <img alt="" aria-hidden="true" className="mx-auto mb-0.5 h-[58px] w-[58px] object-contain" src={iconCalendar} />
              <h3 className="text-[19px] font-black leading-[1.28]">
                You’re invited!
                <span className="block text-[21px]">Weekend Brunch</span>
              </h3>
              <p className="mt-[7px] text-[15px] font-bold text-[#465071]">Sat, May 24</p>
              <p className="mt-3 text-[17px] font-semibold leading-[1.35] text-[#475071]">
                What times work for you?
                <small className="mt-[3px] block text-[14px] font-semibold">Select all that work</small>
              </p>

              <div aria-label="Availability options" className="mt-2.5 grid gap-[7px]">
                {availabilityOptions.map(([day, time]) => {
                  const label = `${day} ${time}`;
                  const selected = selectedTimes.has(label);
                  return (
                    <label
                      className={`grid min-h-11 cursor-pointer grid-cols-[29px_1fr_auto] items-center rounded-[10px] px-[13px] text-left text-[14px] font-black ${selected ? 'bg-[#EEEAFF]' : 'border border-[#E2E4F1] bg-white'}`}
                      key={label}
                    >
                      <input
                        checked={selected}
                        className="sr-only"
                        onChange={() => toggleTime(label)}
                        type="checkbox"
                      />
                      <span className={`grid h-4 w-4 place-items-center rounded border-2 text-[12px] leading-none ${selected ? 'border-[#4B35FF] bg-[#4B35FF] text-white' : 'border-[#C7CBE1] text-white'}`} aria-hidden="true">
                        {selected ? '✓' : ''}
                      </span>
                      <span>{day}</span>
                      <span className="justify-self-end font-bold text-[#28305A]">{time}</span>
                    </label>
                  );
                })}
              </div>

              <button
                className="mt-[15px] min-h-11 w-full rounded-[10px] bg-[#FF625B] text-[16px] font-black text-white shadow-[0_12px_24px_rgba(255,96,91,0.24)]"
                onClick={() => setStatus(`Availability submitted with ${selectedTimes.size} selected times`)}
                type="button"
              >
                Submit availability
              </button>
            </div>
          </div>

          <div className="order-5 w-full max-w-[620px] md:justify-self-start xl:max-w-none xl:justify-self-center">
            <div aria-label="Guest benefits" className="grid w-full max-w-[248px] xl:mt-[83px]">
              {guestFeatures.map(([icon, title, body]) => (
                <article className="grid min-h-24 grid-cols-[60px_1fr] items-center gap-[19px] border-b border-[#E2E4F1] text-left" key={title}>
                  <img alt="" aria-hidden="true" className="h-[60px] w-[60px] object-contain" src={icon} />
                  <div>
                    <h4 className="text-[16px] font-black leading-[1.1]">{title}</h4>
                    <p className="mt-1.5 text-[14px] font-semibold leading-[1.38] text-[#495074]">{body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        <p className="sr-only" role="status">
          {status}
        </p>
      </div>
    </section>
  );
}
